import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InsightsIcon from "@mui/icons-material/Insights";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api"; // adjust if your axios instance path differs

function fmtSeconds(sec) {
  const s = Math.max(0, Number(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m ${r}s`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

function safeNum(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function clamp(n, a, b) {
  return Math.min(b, Math.max(a, n));
}

export default function JambAnalysisPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");
      try {
        // ✅ Backend endpoint:
        // GET /exam/jamb/attempt/:attemptId/analysis
        const res = await api.get(`/exam/jamb/attempt/${attemptId}/analysis`);
        if (!mounted) return;
        setData(res.data);
      } catch (e) {
        if (!mounted) return;
        setErr(
          e?.response?.data?.message ||
            "Unable to load JAMB analysis. Please try again."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (attemptId) load();
    return () => {
      mounted = false;
    };
  }, [attemptId]);

  const computed = useMemo(() => {
    const attempt = data?.attempt || {};
    const subjects = Array.isArray(data?.subjects) ? data.subjects : [];

    // Backend already computes:
    // - normalized_score_400
    // - summary
    // - per-subject score_100
    const subjectRows = subjects
      .slice()
      .sort((a, b) => safeNum(a.seq, 0) - safeNum(b.seq, 0))
      .map((s) => {
        const totalQuestions =
          safeNum(s.total_questions, 0) ||
          safeNum(s.question_count, 0) ||
          safeNum(s.totalQuestions, 0);

        const answered =
          safeNum(s.answered, 0) ||
          safeNum(s.answered_count, 0) ||
          safeNum(s.answeredCount, 0) ||
          Math.min(
            totalQuestions,
            safeNum(s.correct, 0) + safeNum(s.wrong, 0)
          );

        const correct = safeNum(s.correct, 0) || safeNum(s.correct_count, 0);
        const wrong = safeNum(s.wrong, 0) || safeNum(s.wrong_count, 0);

        // Prefer backend time_spent_seconds
        const durationSeconds =
          safeNum(s.time_spent_seconds, 0) ||
          safeNum(s.duration_seconds, 0) ||
          safeNum(s.time_spent_seconds, 0);

        const avgSecPerQ =
          answered > 0 ? Math.round(durationSeconds / answered) : 0;

        // ✅ CRITICAL FIX: Use backend score_100 (source of truth)
        const subjectScore100 =
          safeNum(s.score_100, NaN) ??
          safeNum(s.score100, NaN);

        const finalScore100 = Number.isFinite(subjectScore100)
          ? clamp(subjectScore100, 0, 100)
          : 0;

        return {
          ...s,
          subject_name: s.subject_name || s.subjectName || "Subject",
          seq: safeNum(s.seq, 0),
          totalQuestions,
          answered,
          correct,
          wrong,
          durationSeconds,
          avgSecPerQ,
          subjectScore100: finalScore100,
          status: s.status || "pending",
        };
      });

    const totalScore400 =
      safeNum(data?.normalized_score_400, NaN);

    const finalTotalScore400 = Number.isFinite(totalScore400)
      ? clamp(Math.round(totalScore400), 0, 400)
      : Math.round(
          subjectRows.reduce((sum, r) => sum + safeNum(r.subjectScore100, 0), 0)
        );

    const summary = data?.summary || {};
    const totalQuestions = subjectRows.reduce(
      (sum, r) => sum + safeNum(r.totalQuestions, 0),
      0
    );

    const totalAnswered =
      safeNum(summary.answered, NaN) ??
      subjectRows.reduce((sum, r) => sum + safeNum(r.answered, 0), 0);

    const totalCorrect =
      safeNum(summary.correct, NaN) ??
      subjectRows.reduce((sum, r) => sum + safeNum(r.correct, 0), 0);

    const totalWrong =
      safeNum(summary.wrong, NaN) ??
      subjectRows.reduce((sum, r) => sum + safeNum(r.wrong, 0), 0);

    const totalTimeSeconds =
      safeNum(summary.total_time_seconds, NaN) ??
      subjectRows.reduce((sum, r) => sum + safeNum(r.durationSeconds, 0), 0);

    const avgSecPerQ =
      safeNum(summary.avg_time_per_answer, NaN) ??
      (safeNum(totalAnswered, 0) > 0
        ? Math.round(safeNum(totalTimeSeconds, 0) / safeNum(totalAnswered, 0))
        : 0);

    const attemptStatus = attempt.status || data?.status || "unknown";

    return {
      attempt,
      subjectRows,
      totalScore400: finalTotalScore400,
      totalQuestions,
      totalAnswered: safeNum(totalAnswered, 0),
      totalCorrect: safeNum(totalCorrect, 0),
      totalWrong: safeNum(totalWrong, 0),
      totalTimeSeconds: safeNum(totalTimeSeconds, 0),
      avgSecPerQ: safeNum(avgSecPerQ, 0),
      attemptStatus,
    };
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography>Loading JAMB analysis...</Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (err) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">{err}</Alert>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const attemptTitle =
    computed?.attempt?.title ||
    computed?.attempt?.name ||
    `JAMB Practice Attempt #${attemptId}`;

  const statusChip = (() => {
    const st = String(computed.attemptStatus || "").toLowerCase();
    if (st.includes("complete")) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Completed"
          color="success"
          variant="filled"
        />
      );
    }
    if (st.includes("progress")) {
      return (
        <Chip
          icon={<AccessTimeIcon />}
          label="In Progress"
          color="warning"
          variant="filled"
        />
      );
    }
    if (st.includes("cancel") || st.includes("abandon")) {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Cancelled"
          color="error"
          variant="filled"
        />
      );
    }
    return <Chip label={computed.attemptStatus || "Unknown"} />;
  })();

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              <InsightsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              JAMB Analysis
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {attemptTitle}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {statusChip}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Summary Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Normalized JAMB Score (400-scale)
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 900, mt: 0.5 }}>
                {computed.totalScore400}/400
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={clamp((computed.totalScore400 / 400) * 100, 0, 100)}
                  sx={{ height: 10, borderRadius: 999 }}
                />
              </Box>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                Derived as the sum of per-subject 0–100 scores (4 subjects → 400).
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Questions Summary
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                Answered: {computed.totalAnswered} / {computed.totalQuestions}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                ✅ Correct: <b>{computed.totalCorrect}</b> &nbsp; | &nbsp; ❌ Wrong:{" "}
                <b>{computed.totalWrong}</b>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                Use this to track accuracy across the whole JAMB attempt.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Time Summary
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                Total Time: {fmtSeconds(computed.totalTimeSeconds)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Avg / Answer: <b>{fmtSeconds(computed.avgSecPerQ)}</b>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                Helps users identify time sinks and improve pacing.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Per-subject breakdown */}
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
          Per-Subject Breakdown
        </Typography>

        {computed.subjectRows.length === 0 ? (
          <Alert severity="info">No subject analysis found for this attempt.</Alert>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {computed.subjectRows.map((s) => {
                const pctAnswered =
                  s.totalQuestions > 0
                    ? clamp((s.answered / s.totalQuestions) * 100, 0, 100)
                    : 0;

                return (
                  <Grid item xs={12} md={6} key={`${s.subject_id}-${s.seq}`}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={`Subject ${s.seq}`}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                          {s.subject_name}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                          label={String(s.status).toUpperCase()}
                          size="small"
                          color={
                            String(s.status).toLowerCase().includes("complete")
                              ? "success"
                              : String(s.status).toLowerCase().includes("progress")
                              ? "warning"
                              : "default"
                          }
                        />
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Score (0–100)
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 900 }}>
                            {Math.round(s.subjectScore100)}/100
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Time Spent
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {fmtSeconds(s.durationSeconds)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Avg / Answer: {fmtSeconds(s.avgSecPerQ)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Answered: <b>{s.answered}</b> / {s.totalQuestions}
                            &nbsp;&nbsp;|&nbsp;&nbsp;✅ <b>{s.correct}</b> &nbsp; ❌{" "}
                            <b>{s.wrong}</b>
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pctAnswered}
                              sx={{ height: 8, borderRadius: 999 }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {/* Table for exact metrics */}
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Detailed Metrics Table
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Useful for audit, reporting, and exporting later.
                </Typography>
              </Box>

              <Divider />

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><b>#</b></TableCell>
                      <TableCell><b>Subject</b></TableCell>
                      <TableCell><b>Answered</b></TableCell>
                      <TableCell><b>Correct</b></TableCell>
                      <TableCell><b>Wrong</b></TableCell>
                      <TableCell><b>Time Spent</b></TableCell>
                      <TableCell><b>Avg/Answer</b></TableCell>
                      <TableCell><b>Score (100)</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {computed.subjectRows.map((s) => (
                      <TableRow key={`row-${s.subject_id}-${s.seq}`}>
                        <TableCell>{s.seq}</TableCell>
                        <TableCell>{s.subject_name}</TableCell>
                        <TableCell>
                          {s.answered}/{s.totalQuestions}
                        </TableCell>
                        <TableCell>{s.correct}</TableCell>
                        <TableCell>{s.wrong}</TableCell>
                        <TableCell>{fmtSeconds(s.durationSeconds)}</TableCell>
                        <TableCell>{fmtSeconds(s.avgSecPerQ)}</TableCell>
                        <TableCell>{Math.round(s.subjectScore100)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </>
        )}
      </Paper>
    </Box>
  );
}
